import { ReactNode, useState } from 'react';

import { type RelatedProductsResult } from '@boc/framework/products';
import { createSafeContext } from '@boc/framework/safe-context';
import { CategoryBasicFragment } from '@boc/framework/saleor-graphql';

import {
  TProductBasicInfo,
  TProductDetails,
  TProductVariantBasicInfo,
} from '~/local/products/type';

interface IProductDetailsContext {
  product: TProductDetails;
  category?: CategoryBasicFragment | null;
  variant: TProductVariantBasicInfo;
  setVariant: React.Dispatch<React.SetStateAction<string | null>>;
  relatedProducts?: RelatedProductsResult[];
  isAvailable?: boolean | null;
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
}

const [useProductDetails, DetailsProvider] = createSafeContext<IProductDetailsContext>();

const ProductDetailsProvider = ({
  product,
  category,
  variant,
  setVariant,
  relatedProducts,
  children,
}: { children: ReactNode } & Omit<
  IProductDetailsContext,
  'isAvailable' | 'quantity' | 'setQuantity'
>) => {
  const [quantity, setQuantity] = useState(1);

  return (
    <DetailsProvider
      value={{
        product,
        category,
        variant,
        setVariant,
        relatedProducts,
        isAvailable: product.isAvailable,
        quantity,
        setQuantity,
      }}
    >
      {children}
    </DetailsProvider>
  );
};

interface IProductBasicInfoContext {
  product: TProductBasicInfo;
  variant?: TProductVariantBasicInfo;
  setVariant?: React.Dispatch<React.SetStateAction<string | null>>;
}

const [useProductBasicInfo, Provider] = createSafeContext<IProductBasicInfoContext>();

const ProductBasicInfoProvider = ({
  product,
  variant,
  setVariant,
  children,
}: { children: ReactNode } & IProductBasicInfoContext) => {
  return <Provider value={{ product, variant, setVariant }}>{children}</Provider>;
};

export { useProductDetails, ProductDetailsProvider, useProductBasicInfo, ProductBasicInfoProvider };
